import { compile } from "html-to-text";
import { htmlCanvas } from "@foretagsplatsen/widgetjs";

/**
 * Perform a function (probably a refresh) while the scrolling of a DOM node is preserved.
 *
 * @param {function} fn - Function to perform while preserving the scroll positions
 * @param {function(): DOMElement} [getDOMNode] - Function to retrieve the DOM node for
 *   which to preserve scroll positions
 */
export function withScrollRetained({ fn, getDOMNode = () => document.body }) {
	let node = getDOMNode();
	const { scrollLeft, scrollTop } = node;

	fn();

	// Get the DOM node a second time so that if the dom is updated, we restore the scroll on the fresh node
	node = getDOMNode();
	node.scrollLeft = scrollLeft;
	node.scrollTop = scrollTop;
}

/**
 * Perform a function (probably a refresh) while the scrolling of #viewport is preserved.
 *
 * @param {function} fn - Function to perform while preserving the scroll positions
 */
export function withViewportScrollRetained(fn) {
	withScrollRetained({
		fn,
		getDOMNode: () => document.getElementById("viewport"),
	});
}

/**
 * Return a function that takes some HTML string as parameter and
 * returns a string containing the text of the input string as a
 * single line. Paragraphs of the input are separated by `separator`.
 *
 * @param {string} separator - A string used to separate lines of
 *   different paragraphs
 * @return {function(string): string} The first parameter of the
 *   function is an HTML string
 */
export function makeHTMLToSingleLineConverter({ separator = "⏎" } = {}) {
	const convert = compile({
		selectors: [
			// When the HTML contains a <p>, take the paragraph's content
			// and suffix it with `separator`:
			{
				selector: "p",
				format: "inlineSurround",
				options: { suffix: separator },
			},
		],
	});

	return (input) => {
		const output = convert(input);

		return output.at(-1) === separator ? output.slice(0, -1) : output;
	};
}

/**
 * Convert a renderable to HTML string.
 *
 * This is useful to integrate with 3rd party libraries that require HTML.
 *
 * @param {Renderable} renderable - The renderable to convert
 * @return {string}
 */
export function convertRenderableToHTML(renderable) {
	const html = htmlCanvas();
	const ref = {};

	html.render({ ref }, renderable);

	const documentFragment = ref.current;

	let result = "";

	for (const child of documentFragment.childNodes) {
		result +=
			child.nodeType === child.TEXT_NODE
				? child.textContent
				: child.outerHTML;
	}

	return result;
}

/**
 * Return true if and only if `element` is attached to the DOM. This
 * crosses shadow DOM boundaries.
 *
 * @param {React} element - The element to check for existence in the DOM
 * @return {boolean}
 */
export function isInDocument(element) {
	let currentElement = element;

	while (currentElement?.parentNode) {
		if (currentElement.parentNode === document) {
			return true;
		} else if (currentElement.parentNode instanceof DocumentFragment) {
			currentElement = currentElement.parentNode.host;
		} else {
			currentElement = currentElement.parentNode;
		}
	}

	return false;
}
